import { LoadingIcon } from '@assets/icons';
import { Select } from '@components/form';
import { Group } from '@components/layout';
import { LogType, LogsScreen } from '@components/logsScreen';
import { useGetDatasetLogLazyQuery, useGetDatasetRunsQuery } from '@generated/graphql';
import { Container, MenuItem } from '@mui/material';
import { useEffect, useState } from 'react';

interface JobsManagerJobPreviewLogsScreenProps {
  datasetId: string;
  workspaceId: string;
}

export const JobsManagerJobPreviewLogsScreen = ({
  datasetId,
  workspaceId,
}: JobsManagerJobPreviewLogsScreenProps) => {
  const [state, setState] = useState('all');
  const [run, setRun] = useState('0');

  const { data: datasetRunsData, loading: getDatasetRunsLoading } = useGetDatasetRunsQuery({
    variables: { datasetId, workspaceId },
  });
  const [queryDatasetLogs, { data: datasetLogsData, loading: datasetLogsLoading }] =
    useGetDatasetLogLazyQuery();
  const datasetRuns = datasetRunsData?.getDatasetRuns;

  const states = (datasetRuns || [])
    .map((r) => r.state)
    .filter((s, index, array) => array.indexOf(s) === index && s !== 'cancelled');

  const datasetLogsString = datasetLogsData?.getDatasetLog.log || '[]';
  const datasetLogs = JSON.parse(`${datasetLogsString}`) as LogType[];

  const filteredRuns =
    state === 'all' ? datasetRuns ?? [] : (datasetRuns ?? []).filter((r) => r.state === state);
  const sortedRuns = filteredRuns.map((r) => r.run).sort((a, b) => a - b);
  const runOptions = sortedRuns.map((o) => o.toString());

  // If the current run drops out of the filtered set, snap to the first option.
  useEffect(() => {
    if (sortedRuns.length === 0) return;
    if (!sortedRuns.includes(Number(run))) {
      setRun(sortedRuns[0].toString());
    }
  }, [sortedRuns, run]);

  // Fetch logs whenever the selected run (or the underlying data) changes.
  useEffect(() => {
    if (!datasetRuns?.length || !run) return;
    const runId = datasetRuns.find((r) => r.run === Number(run))?.runId;
    void queryDatasetLogs({ variables: { workspaceId, datasetId, runId } });
  }, [datasetRuns, run, queryDatasetLogs, workspaceId, datasetId]);

  const handleRunChange = ({ target: { value } }) => setRun(value as string);
  const handleStateChange = ({ target: { value } }) => setState(value as string);

  if (getDatasetRunsLoading) {
    return (
      <Container
        maxWidth="lg"
        sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <LoadingIcon size={16} />
      </Container>
    );
  }

  return (
    <LogsScreen
      logs={datasetLogs}
      loading={datasetLogsLoading}
      header={
        <Group gap={4}>
          <Select
            label="State"
            value={state}
            onChange={handleStateChange}
            sx={{ minWidth: '200px', textTransform: 'capitalize' }}
          >
            {['all', ...states].map((s) => (
              <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>
                {s}
              </MenuItem>
            ))}
          </Select>
          <Select label="Run" value={run} onChange={handleRunChange} sx={{ minWidth: '200px' }}>
            {runOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </Group>
      }
    />
  );
};
